/**
 * Publisher caps — dash data-layer client.
 *
 * Thin typed wrapper over `apiClient` for the `/api/v1/publisher-caps`
 * surface (feature #135, OPUS-135). Mirrors the contract declared in
 * `apps/api/src/routes/publisher-caps.ts`.
 *
 * Zero external deps beyond the root `apiClient` — no React, no TanStack
 * — so the module is reusable from pure-logic tests and any future
 * server-side smoke harness.
 *
 * Endpoints (see backend file for the authoritative spec):
 *   POST   /                   upsert a cap row
 *   GET    /:affiliate_id      list cap rows for a publisher
 *   POST   /increment          bump consumed; learn over_cap / remaining
 *   POST   /check              decide { allowed, remaining } for a
 *                              (publisher, offer) pair
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Canonical domain types — 1:1 with the backend enums. Kept frozen and
// exported so UI + tests share the same constant surface.
// ---------------------------------------------------------------------------

export const CAP_TYPES = ["conversions", "payout_cents"] as const;
export type CapType = (typeof CAP_TYPES)[number];

export const WINDOWS = ["daily", "monthly", "lifetime"] as const;
export type Window = (typeof WINDOWS)[number];

export const STATUSES = ["active", "paused"] as const;
export type Status = (typeof STATUSES)[number];

export const CAP_TYPE_LABELS: Record<CapType, string> = {
	conversions: "Conversions",
	payout_cents: "Payout (cents)",
};

export const WINDOW_LABELS: Record<Window, string> = {
	daily: "Daily",
	monthly: "Monthly",
	lifetime: "Lifetime",
};

export const STATUS_LABELS: Record<Status, string> = {
	active: "Active",
	paused: "Paused",
};

export const WINDOW_HINTS: Record<Window, string> = {
	daily: "Resets every UTC midnight.",
	monthly: "Resets on the first of each UTC month.",
	lifetime: "Never resets — one running counter forever.",
};

// Kept in sync with the backend constants.
export const MAX_ID_LENGTH = 128;
export const MAX_CAP_LIMIT = 2_000_000_000;
export const MAX_INCREMENT_QTY = 1_000_000;

// ---------------------------------------------------------------------------
// API row / response shapes.
// ---------------------------------------------------------------------------

export interface PublisherCapView {
	id: string;
	affiliate_id: string;
	offer_id: string | null;
	offer_id_norm: string;
	cap_type: CapType;
	cap_limit: number;
	window_key: Window;
	consumed: number;
	resets_at: number;
	status: Status;
	created_at: number;
	updated_at: number;
}

export interface ListCapsResponse {
	affiliate_id: string;
	caps: PublisherCapView[];
}

export interface IncrementResponse {
	id: string;
	affiliate_id: string;
	offer_id: string | null;
	cap_type: CapType;
	window: Window;
	cap_limit: number;
	consumed: number;
	remaining: number;
	over_cap: boolean;
	status: Status;
}

export interface CheckMatchView {
	cap_type: CapType;
	window_key: Window;
	offer_id: string | null;
	cap_limit: number;
	consumed: number;
	remaining: number;
	status: Status;
}

export interface CheckResponse {
	allowed: boolean;
	remaining: number | null;
	caps: CheckMatchView[];
}

// ---------------------------------------------------------------------------
// Pure helpers — used by the page (for bars + badges) and the tests (to
// lock the invariants in place).
// ---------------------------------------------------------------------------

export function isCapType(v: unknown): v is CapType {
	return typeof v === "string" && (CAP_TYPES as readonly string[]).includes(v);
}

export function isWindow(v: unknown): v is Window {
	return typeof v === "string" && (WINDOWS as readonly string[]).includes(v);
}

export function isStatus(v: unknown): v is Status {
	return typeof v === "string" && (STATUSES as readonly string[]).includes(v);
}

export function remainingFor(row: {
	cap_limit: number;
	consumed: number;
}): number {
	const delta = row.cap_limit - row.consumed;
	return delta > 0 ? delta : 0;
}

export function isOverCap(row: {
	cap_limit: number;
	consumed: number;
}): boolean {
	return row.consumed >= row.cap_limit;
}

/**
 * Progress-bar fill percentage (0..100). cap_limit of 0 snaps to 100%
 * so the bar flips rose to highlight the hard zero — same policy the
 * backend uses for isOverCap({0,0}).
 */
export function filledPct(row: {
	cap_limit: number;
	consumed: number;
}): number {
	if (row.cap_limit <= 0) return 100;
	const raw = (row.consumed / row.cap_limit) * 100;
	if (!Number.isFinite(raw)) return 100;
	if (raw < 0) return 0;
	if (raw > 100) return 100;
	return Math.round(raw * 100) / 100;
}

// ---------------------------------------------------------------------------
// Threshold classifier — drives the 3-tier usage bar coloring in the FS-822
// operator table. The bar stays emerald while there's plenty of runway,
// shifts to amber in the 60-90% warning band, then locks red once the cap
// is within 10% of its ceiling (or over).
// ---------------------------------------------------------------------------

export type ThresholdLevel = "ok" | "warn" | "hot";

export function classifyThreshold(pct: number): ThresholdLevel {
	if (!Number.isFinite(pct) || pct < 0) return "ok";
	if (pct >= 90) return "hot";
	if (pct >= 60) return "warn";
	return "ok";
}

export const THRESHOLD_BAR_CLASS: Record<ThresholdLevel, string> = {
	ok: "bg-emerald-500",
	warn: "bg-amber-500",
	hot: "bg-red-500",
};

export const THRESHOLD_TEXT_CLASS: Record<ThresholdLevel, string> = {
	ok: "text-emerald-700",
	warn: "text-amber-700",
	hot: "text-red-600",
};

export function thresholdBarClass(pct: number): string {
	return THRESHOLD_BAR_CLASS[classifyThreshold(pct)];
}

/**
 * Formats a payout_cents value as a $X.YZ string for display. Non-payout
 * cap types return the plain integer.
 */
export function formatCapValue(
	value: number,
	capType: CapType,
): string {
	if (capType === "payout_cents") {
		const dollars = value / 100;
		return `$${dollars.toLocaleString(undefined, {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})}`;
	}
	return value.toLocaleString();
}

// ---------------------------------------------------------------------------
// Validation — hand-rolled `.safeParse` surface (zero dep on zod).
// ---------------------------------------------------------------------------

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

export interface CapEditorInput {
	affiliateId: string;
	offerId: string;
	capType: CapType | string;
	window: Window | string;
	limit: number | string;
	status?: Status | string;
}

export interface CapEditorParsed {
	affiliate_id: string;
	offer_id: string | null;
	cap_type: CapType;
	window: Window;
	limit: number;
	status: Status;
}

export const capEditorSchema = {
	safeParse(input: CapEditorInput): ParseResult<CapEditorParsed> {
		const errors: Record<string, string> = {};

		const affiliateId =
			typeof input.affiliateId === "string" ? input.affiliateId.trim() : "";
		if (affiliateId.length === 0) {
			errors.affiliateId = "Affiliate ID is required.";
		} else if (affiliateId.length > MAX_ID_LENGTH) {
			errors.affiliateId = `Affiliate ID must be ${MAX_ID_LENGTH} chars or fewer.`;
		} else if (/[\x00-\x1f]/.test(affiliateId)) {
			errors.affiliateId = "Affiliate ID contains invalid control characters.";
		}

		// offer_id is optional — empty means wildcard / "every offer".
		const rawOffer =
			typeof input.offerId === "string" ? input.offerId.trim() : "";
		let offer_id: string | null = null;
		if (rawOffer.length > 0) {
			if (rawOffer.length > MAX_ID_LENGTH) {
				errors.offerId = `Offer ID must be ${MAX_ID_LENGTH} chars or fewer.`;
			} else if (/[\x00-\x1f]/.test(rawOffer)) {
				errors.offerId = "Offer ID contains invalid control characters.";
			} else {
				offer_id = rawOffer;
			}
		}

		if (!isCapType(input.capType)) {
			errors.capType = "Cap type must be conversions or payout_cents.";
		}
		if (!isWindow(input.window)) {
			errors.window = "Window must be daily, monthly, or lifetime.";
		}

		const limitNum =
			typeof input.limit === "number"
				? input.limit
				: Number.parseInt(String(input.limit).trim(), 10);
		if (!Number.isFinite(limitNum) || !Number.isInteger(limitNum)) {
			errors.limit = "Limit must be a whole number.";
		} else if (limitNum <= 0) {
			errors.limit = "Limit must be greater than zero.";
		} else if (limitNum > MAX_CAP_LIMIT) {
			errors.limit = `Limit must be ${MAX_CAP_LIMIT.toLocaleString()} or fewer.`;
		}

		let status: Status = "active";
		if (input.status !== undefined) {
			if (!isStatus(input.status)) {
				errors.status = "Status must be active or paused.";
			} else {
				status = input.status;
			}
		}

		if (Object.keys(errors).length > 0) return { success: false, errors };
		return {
			success: true,
			data: {
				affiliate_id: affiliateId,
				offer_id,
				cap_type: input.capType as CapType,
				window: input.window as Window,
				limit: limitNum,
				status,
			},
		};
	},
};

// ---------------------------------------------------------------------------
// Remote operations.
// ---------------------------------------------------------------------------

const BASE = "/publisher-caps";

export interface UpsertCapArgs {
	id?: string;
	affiliate_id: string;
	offer_id?: string | null;
	cap_type: CapType;
	limit: number;
	window: Window;
	consumed?: number;
	resets_at?: number;
	status?: Status;
}

export async function upsertCap(args: UpsertCapArgs): Promise<PublisherCapView> {
	return apiClient.post<PublisherCapView>(BASE, args);
}

export async function listCapsForAffiliate(
	affiliateId: string,
): Promise<PublisherCapView[]> {
	try {
		const res = await apiClient.get<ListCapsResponse>(
			`${BASE}/${encodeURIComponent(affiliateId)}`,
		);
		return res.caps ?? [];
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return [];
		throw err;
	}
}

export interface IncrementCapArgs {
	affiliate_id: string;
	offer_id?: string | null;
	cap_type: CapType;
	window: Window;
	qty?: number;
}

export async function incrementCap(
	args: IncrementCapArgs,
): Promise<IncrementResponse> {
	return apiClient.post<IncrementResponse>(`${BASE}/increment`, args);
}

export interface CheckCapArgs {
	affiliate_id: string;
	offer_id?: string | null;
}

export async function checkCap(args: CheckCapArgs): Promise<CheckResponse> {
	return apiClient.post<CheckResponse>(`${BASE}/check`, args);
}

// ---------------------------------------------------------------------------
// Query keys — consumed by PublisherCapsPage.
// ---------------------------------------------------------------------------

export const publisherCapsQueryKeys = {
	all: ["publisher-caps"] as const,
	byAffiliate: (affiliateId: string) =>
		[...publisherCapsQueryKeys.all, "affiliate", affiliateId] as const,
};
